<script setup>
const props = defineProps({
  textBottom: String,
  runTime: String,
  currentTime: Number,
  hasAnotherBottomBlock: Boolean,
})

const { textBottom, runTime, currentTime, hasAnotherBottomBlock } = toRefs(props)

const text = computed(() => textBottom.value?.split('<br>') ?? [])
const runTimeRanges = computed(() => {
  if (!runTime.value) {
    return []
  }

  return runTime.value.replace(/\s/g, '')
    .split(',')
    .map(s => s.split('-').map(Number))
    .map(([start, end]) => ([Math.max(Number(start) - 1, 0), Number(end) + 1])) ?? []
})
const isVisible = computed(() => {
  if (runTimeRanges.value.length === 0) {
    return true
  }

  return runTimeRanges.value.some(([start, end]) => currentTime.value >= start && currentTime.value <= end)
})
</script>

<template>
  <Transition name="fade">
    <div v-show="isVisible" class="video-animation" :class="{ 'video-animation__side-by-side': hasAnotherBottomBlock }">
      <img alt="Alrosa" class="video-animation__left" src="/images/alrosa.svg" />
      <img alt="Diamonds" class="video-animation__right" src="/images/diamonds.svg" />
      <div class="video-animation__info">
        <p v-if="text[0]" class="video-animation__text" v-html="text[0]"/>
        <p v-if="text[1]" class="video-animation__text video-animation__text_canela" v-html="text[1]"/>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
@use '/assets/scss/vars' as *;
@use '/assets/scss/mixins' as *;

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.video-animation {
  &__side-by-side {
    .video-animation__left,
    .video-animation__right {
      display: none;

      @media ($sm) {
        display: block;
      }
    }

    .video-animation__info {
      bottom: 85px;
      min-width: 300px;

      @media ($sm) {
        bottom: 113px;
        padding: 16px 8px;
      }
    }
  }

  &__left,
  &__right,
  &__info {
    position: absolute;
    z-index: 6;
    left: 50%;
    transform: translate(-50%, 0);
  }

  &__left {
    top: calc(52px + 60px);
    width: 58.14%;

    @media ($sm) {
      top: 50%;
      transform: translate(0, -50%);
      width: 36.5%;
      left: 32px;
    }

    @media ($md) {
      left: 72px;
      max-width: 315px;
    }

    @media ($lg) {
      left: 144px;
    }

    @media ($xl) {
      left: 180px;
      max-width: 428px;
    }
  }

  &__right {
    bottom: 64px;
    width: 79.21%;

    @media ($sm) {
      bottom: auto;
      top: 50%;
      right: 32px;
      left: auto;
      width: 49.48%;
      transform: translate(0, -50%);
    }

    @media ($md) {
      right: 72px;
      max-width: 428px;
    }

    @media ($lg) {
      right: 144px;
    }

    @media ($xl) {
      right: 180px;
      max-width: 580px;
    }
  }

  &__info {
    bottom: 28.334%;
    text-align: center;
    color: #fff;

    @media ($sm) {
      bottom: 112px;
    }

    @media ($md) {
      bottom: 40px;
    }
  }

  &__text {
    letter-spacing: 0.1em;
    line-height: 20px;
    font-size: 15px;
    font-family: 'Canela', serif;
    font-variant-numeric: lining-nums;

    @media ($sm) {
      line-height: 28px;
      font-size: 22px;
    }

    @media ($xl) {
      line-height: 44px;
      font-size: 32px;
    }
  }
}
</style>
